import type { ReactElement } from 'react';
import React, { useState } from 'react';

import { PaletteView } from 'src/modules/designStudio/components/DesignMenu/Menu/TemplateMenu/PaletteView';
import { TemplatesView } from 'src/modules/designStudio/components/DesignMenu/Menu/TemplateMenu/TemplatesView';
import { TemplateProductsSummary } from 'src/modules/designStudio/components/DesignMenu/Menu/TemplateMenu/products/TemplateProductsSummary';
import { useTemplateProductBreakdown } from 'src/modules/designStudio/components/DesignMenu/Menu/TemplateMenu/products/useTemplateProductBreakdown';
import { useTemplateTrackingData } from 'src/modules/designStudio/components/DesignMenu/Menu/TemplateMenu/useTemplateTrackingData';
import type { TemplatePaletteConfig } from 'src/modules/designStudio/helpers/data/PaletteStyles';
import { TEMPLATE_STYLES_MANAGER } from 'src/modules/designStudio/helpers/data/TemplateStyles';
import { useBuildingDesigner } from 'src/modules/designStudio/helpers/machete/useBuildingDesigner';
import { buttonPressed } from 'src/shared/telemetry';

enum TemplateViewMode {
  VIEW_TEMPLATES,
  VIEW_PALETTES,
  VIEW_PRODUCTS_SUMMARY,
}

export const TemplateMenu = ({ closeMenu }: { closeMenu: () => void }) => {
  const templates = TEMPLATE_STYLES_MANAGER;
  const { mutateBuilding } = useBuildingDesigner();

  const [viewMode, setViewMode] = useState(TemplateViewMode.VIEW_TEMPLATES);

  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState<
    number | undefined
  >(undefined);
  const [selectedPaletteIndex, setSelectedPaletteIndex] = useState<number>(0);
  const [isMiniPaletteRowVisible, setIsMiniPaletteRowVisible] =
    useState<boolean>(false);

  const palettes =
    selectedTemplateIndex === undefined
      ? []
      : templates.getByIndex(selectedTemplateIndex).palettes;

  const { commonTemplatesTrackingData, commonRealProductsTrackingData } =
    useTemplateTrackingData();

  const { isEmpty } = useTemplateProductBreakdown();

  const actions = {
    onMenuClosed: () => {
      closeMenu();
      buttonPressed({
        ...commonTemplatesTrackingData,
        track_id: 'close-design-template-panel',
        button_text: '',
      });
    },
    onTemplateSelected: (index: number) => {
      setSelectedTemplateIndex(index);
      setSelectedPaletteIndex(0);
      setIsMiniPaletteRowVisible(true);
      const template = templates.getByIndex(index);
      mutateBuilding(async mutations => {
        const templateStyle = { ...template.style };
        await mutations.applyDesignConfig({
          ...templateStyle,
          designName: 'user-custom-template',
        });
      });
      buttonPressed({
        ...commonTemplatesTrackingData,
        track_id: 'design-template-design-selected',
        button_text: `${template.style.designName ?? ''}`,
      });
    },
    onPaletteSelected: (index: number) => {
      setSelectedPaletteIndex(index);
      mutateBuilding(async mutations => {
        await mutations.applyPalette(
          JSON.parse(JSON.stringify(palettes[index])) as TemplatePaletteConfig,
        );
      });
      buttonPressed({
        ...commonTemplatesTrackingData,
        button_location:
          viewMode === TemplateViewMode.VIEW_TEMPLATES
            ? 'design-template-mini-color-palette-selected'
            : 'design-template-color-palette-scroll',
        track_id:
          viewMode === TemplateViewMode.VIEW_TEMPLATES
            ? 'design-template-mini-color-palette-selected'
            : 'design-template-full-color-palette-selected',
        button_text: `${palettes[index].paletteName ?? ''}`,
      });
    },
    onPaletteRowShowMore: () => {
      setViewMode(TemplateViewMode.VIEW_PALETTES);
      buttonPressed({
        ...commonTemplatesTrackingData,
        track_id: 'additional-color-palettes',
        button_text: `${palettes.length - 3}`,
      });
    },
    onPaletteRowCancel: () => {
      setIsMiniPaletteRowVisible(false);
      buttonPressed({
        ...commonTemplatesTrackingData,
        track_id: 'hide-mini-palette-row',
        button_text: '',
      });
    },
    onPaletteViewBack: () => {
      setViewMode(TemplateViewMode.VIEW_TEMPLATES);
    },
    onViewProducts: () => {
      setViewMode(TemplateViewMode.VIEW_PRODUCTS_SUMMARY);
      buttonPressed({
        ...commonRealProductsTrackingData,
        track_id: 'view-products-palette-button',
        button_text: 'View products in palette',
      });
    },
    [TemplateViewMode.VIEW_PRODUCTS_SUMMARY]: {
      onBack: () => {
        setViewMode(TemplateViewMode.VIEW_TEMPLATES);
      },
    },
  };

  /**
   * We only want to show the button that says: 'Show Real Product in Template'
   * when at least one product can be shown. Due to product filtering its possible none of the products
   * in the template should be shown to the user, in which case we shouldn't show the button.
   */

  const viewByMode: Record<TemplateViewMode, ReactElement> = {
    [TemplateViewMode.VIEW_TEMPLATES]: (
      <TemplatesView
        closeMenu={actions.onMenuClosed}
        /**
         * We only want to show the button that says: 'Show Real Product in Template'
         * when at least one product can be shown. Due to product filtering its possible none of the products
         * in the template should be shown to the user, in which case we shouldn't show the button.
         */
        doShowViewProductsButton={isEmpty}
        isMiniPaletteRowVisible={isMiniPaletteRowVisible}
        onPaletteRowCancel={actions.onPaletteRowCancel}
        onPaletteRowShowMore={actions.onPaletteRowShowMore}
        onPaletteSelected={actions.onPaletteSelected}
        onTemplateSelected={actions.onTemplateSelected}
        onViewProducts={actions.onViewProducts}
        palettes={palettes}
        selectedPaletteIndex={selectedPaletteIndex}
        selectedTemplateIndex={selectedTemplateIndex}
        templates={templates.getArray()}
      />
    ),
    [TemplateViewMode.VIEW_PALETTES]: (
      <PaletteView
        closeMenu={actions.onMenuClosed}
        onPaletteSelected={actions.onPaletteSelected}
        onPaletteViewBack={actions.onPaletteViewBack}
        onViewProducts={actions.onViewProducts}
        palettes={palettes}
        selectedPaletteIndex={selectedPaletteIndex}
      />
    ),
    [TemplateViewMode.VIEW_PRODUCTS_SUMMARY]: (
      <TemplateProductsSummary
        onBack={actions[TemplateViewMode.VIEW_PRODUCTS_SUMMARY].onBack}
        onCloseMenu={actions.onMenuClosed}
      />
    ),
  };

  return viewByMode[viewMode];
};
